import React, { useEffect, useMemo, useState } from 'react';

import { Autocomplete, Box, TextField } from '@mui/material';
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';

import PageHeader from '../../components/pageHeader';
import { formatDatePtBr } from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import '../css/plantsInfoBI.css';

const allOption = 'all';

const getOptionLabel = (option, allLabel) => option === allOption ? allLabel : option;

const getUniqueValues = (rows, field) => [...new Set(rows.map((item) => item[field]))];

const InformacoesUsinasBI = () => {
    const [usinasInfo, setUsinasInfo] = useState([]);
    const [selectedSpe, setSelectedSpe] = useState(allOption);
    const [selectedUsina, setSelectedUsina] = useState(allOption);
    const [selectedCc, setSelectedCc] = useState(allOption);

    useEffect(() => {
        async function fetchReportData () {
            const usinasInfoResponse = await fetchFlask('listarUsinasInfo');
            setUsinasInfo(usinasInfoResponse);
        }

        fetchReportData();
    }, []);

    const spes = useMemo(() => {
        const rows = usinasInfo
            .filter((item) => selectedUsina === allOption || item.usina === selectedUsina)
            .filter((item) => selectedCc === allOption || item.cc === selectedCc);

        return getUniqueValues(rows, 'SPE');
    }, [usinasInfo, selectedUsina, selectedCc]);

    const usinas = useMemo(() => {
        const rows = usinasInfo
            .filter((item) => selectedSpe === allOption || item.SPE === selectedSpe)
            .filter((item) => selectedCc === allOption || item.cc === selectedCc);

        return getUniqueValues(rows, 'usina');
    }, [usinasInfo, selectedSpe, selectedCc]);

    const ccs = useMemo(() => {
        const rows = usinasInfo
            .filter((item) => selectedSpe === allOption || item.SPE === selectedSpe)
            .filter((item) => selectedUsina === allOption || item.usina === selectedUsina);

        return getUniqueValues(rows, 'cc');
    }, [usinasInfo, selectedSpe, selectedUsina]);

    useEffect(() => {
        if (selectedSpe !== allOption && !spes.includes(selectedSpe)) {
            setSelectedSpe(allOption);
        }
    }, [selectedSpe, spes]);

    useEffect(() => {
        if (selectedUsina !== allOption && !usinas.includes(selectedUsina)) {
            setSelectedUsina(allOption);
        }
    }, [selectedUsina, usinas]);

    useEffect(() => {
        if (selectedCc !== allOption && !ccs.includes(selectedCc)) {
            setSelectedCc(allOption);
        }
    }, [selectedCc, ccs]);

    const filteredUsinasInfo = useMemo(() => {
        return usinasInfo
            .filter((item) => selectedSpe === allOption || item.SPE === selectedSpe)
            .filter((item) => selectedUsina === allOption || item.usina === selectedUsina)
            .filter((item) => selectedCc === allOption || item.cc === selectedCc);
    }, [usinasInfo, selectedSpe, selectedUsina, selectedCc]);

    const kpis = useMemo(() => {
        return {
            usinas: filteredUsinasInfo.length,
            clientes: getUniqueValues(filteredUsinasInfo, 'cliente').length,
            ccs: getUniqueValues(filteredUsinasInfo, 'cc').length,
            spes: getUniqueValues(filteredUsinasInfo, 'SPE').length
        };
    }, [filteredUsinasInfo]);

    const selectedSpeLabel = selectedSpe === allOption ? 'Todas as SPEs' : selectedSpe;
    const selectedUsinaLabel = selectedUsina === allOption ? 'Todas as usinas' : selectedUsina;
    const selectedCcLabel = selectedCc === allOption ? 'Todas as contas contrato' : selectedCc;

    return (
        <Box className='mainBox plants-info-bi-page'>
            <PageHeader>Informações das Usinas</PageHeader>

            <Box className='plants-info-bi-shell'>
                <Box className='plants-info-bi-hero'>
                    <Box className='plants-info-bi-actions'>
                        <Autocomplete
                            className='plants-info-bi-filter'
                            disableClearable
                            options={[allOption, ...spes]}
                            value={selectedSpe}
                            onChange={(event, value) => setSelectedSpe(value)}
                            getOptionLabel={(option) => getOptionLabel(option, 'Todas')}
                            renderInput={(params) => <TextField {...params} label='SPE' />}
                        />

                        <Autocomplete
                            className='plants-info-bi-filter'
                            disableClearable
                            options={[allOption, ...usinas]}
                            value={selectedUsina}
                            onChange={(event, value) => setSelectedUsina(value)}
                            getOptionLabel={(option) => getOptionLabel(option, 'Todas')}
                            renderInput={(params) => <TextField {...params} label='Usina' />}
                        />

                        <Autocomplete
                            className='plants-info-bi-filter'
                            disableClearable
                            options={[allOption, ...ccs]}
                            value={selectedCc}
                            onChange={(event, value) => setSelectedCc(value)}
                            getOptionLabel={(option) => getOptionLabel(option, 'Todos')}
                            renderInput={(params) => <TextField {...params} label='Conta contrato' />}
                        />
                    </Box>
                </Box>

                <Box className='plants-info-bi-kpi-grid'>
                    <Box className='plants-info-bi-kpi is-primary'>
                        <HomeWorkRoundedIcon />
                        <Box>
                            <span>Usinas filtradas</span>
                            <strong>{kpis.usinas}</strong>
                        </Box>
                    </Box>

                    <Box className='plants-info-bi-kpi'>
                        <PersonRoundedIcon />
                        <Box>
                            <span>Clientes</span>
                            <strong>{kpis.clientes}</strong>
                        </Box>
                    </Box>

                    <Box className='plants-info-bi-kpi'>
                        <BadgeRoundedIcon />
                        <Box>
                            <span>Contas contrato</span>
                            <strong>{kpis.ccs}</strong>
                        </Box>
                    </Box>

                    <Box className='plants-info-bi-kpi'>
                        <AccountTreeRoundedIcon />
                        <Box>
                            <span>SPEs</span>
                            <strong>{kpis.spes}</strong>
                        </Box>
                    </Box>
                </Box>

                <Box className='plants-info-bi-table-panel'>
                    <Box className='plants-info-bi-table-toolbar'>
                        <Box>
                            <span><AccountTreeRoundedIcon /> SPE</span>
                            <strong>{selectedSpeLabel}</strong>
                        </Box>
                        <Box>
                            <span><FilterAltRoundedIcon /> Usina</span>
                            <strong>{selectedUsinaLabel}</strong>
                        </Box>
                        <Box>
                            <span><DescriptionRoundedIcon /> Conta contrato</span>
                            <strong>{selectedCcLabel}</strong>
                        </Box>
                    </Box>

                    <Box className='plants-info-bi-table-scroll'>
                        <table className='plants-info-bi-table'>
                            <thead>
                                <tr>
                                    <th>Usina</th>
                                    <th>Nome</th>
                                    <th>Cliente</th>
                                    <th>Documento</th>
                                    <th>CC</th>
                                    <th>Endereço</th>
                                    <th>Início</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsinasInfo.map((item, index) => (
                                    <tr key={`${item.usina}-${item.cc}-${item.documento}-${index}`}>
                                        <td>{item.usina}</td>
                                        <td>{item.SPE}</td>
                                        <td>{item.cliente}</td>
                                        <td>{item.documento}</td>
                                        <td>{item.cc}</td>
                                        <td>{item.endereco}</td>
                                        <td>
                                            <span className='plants-info-bi-date'>
                                                <CalendarMonthRoundedIcon />
                                                {formatDatePtBr(item.inicio)}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default InformacoesUsinasBI;
